import React, { FC, ReactNode, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerDropdown from 'app/components/datepicker-dropdown';
import clsx from 'clsx';
import DatePickerHeaderMonthPeriod from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderMonthPeriod';
import { useTranslation } from 'react-i18next';
import useBoolean from 'utils/hooks/useBoolean';
import { formatDate } from 'app/utils/date';

type Props = {
  date: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  clearButtonTooltipContent?: string | ReactNode;
  className?: {
    wrapper?: string;
    placeholder?: string;
  };
  dataCy?: {
    wrapper?: string;
    day?: string;
    placeholder?: string;
  };
  isDisabled?: boolean;
  withClearButton?: boolean;
  minDate?: Dayjs | null;
};

const SingleDatePicker: FC<Props> = ({
  date,
  onChange,
  clearButtonTooltipContent,
  className,
  dataCy,
  isDisabled,
  withClearButton,
  minDate,
}) => {
  const [isOpen, setIsOpen] = useBoolean(false);
  const [dateTemp, setDateTemp] = useState<Dayjs | null>(date);

  const { t } = useTranslation();

  const handleClearDate = () => {
    setDateTemp(null);
    onChange(null);
  };

  const label = dateTemp ? formatDate(dateTemp) : t('selectDate');

  const selectedDate = date ? date.toDate() : null;

  const onDateSelect = (date: Date | [Date | null, Date | null] | null) => {
    if (date instanceof Date) {
      const selectedDate = dayjs(date);
      setDateTemp(selectedDate);
      onChange(selectedDate);
      setIsOpen.off();
    }
  };

  return (
    <div
      className={clsx(
        'tw-relative tw-inline-flex tw-w-full tw-flex-col',
        className?.wrapper,
      )}
      data-cy={dataCy?.wrapper}
    >
      <DatePickerDropdown
        isDisabled={isDisabled}
        dataCy={dataCy}
        placeholderClassname={clsx('tw-h-10 tw-w-full', className?.placeholder)}
        contentClassname="tw-min-w-max tw-max-w-xs"
        isOpen={isOpen}
        onOpenChange={setIsOpen.toggle}
        label={label}
        selected={selectedDate}
        selectsRange={false}
        showMonthDropdown={false}
        showYearDropdown={false}
        onChange={(val) => {
          onDateSelect(val as Date | [Date | null, Date | null] | null);
        }}
        renderCustomHeader={(headerProps) => (
          <DatePickerHeaderMonthPeriod {...headerProps} />
        )}
        onClear={withClearButton ? handleClearDate : undefined}
        clearButtonTooltipContent={clearButtonTooltipContent}
        minDate={minDate?.toDate()}
      />
    </div>
  );
};

export default SingleDatePicker;
